import { format, formatDistanceToNow } from 'date-fns';
import { enUS } from 'date-fns/locale';

// Para birimi formatı - USA format
export const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '-';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Tarih formatı - USA format
export const formatDate = (date, formatString = 'MMM dd, yyyy') => {
  if (!date) return '-';
  
  try {
    return format(new Date(date), formatString, { locale: enUS });
  } catch (error) {
    return '-';
  }
};

// Tarih ve saat formatı - USA format
export const formatDateTime = (date) => {
  if (!date) return '-';
  
  try {
    return format(new Date(date), 'MMM dd, yyyy h:mm a', { locale: enUS });
  } catch (error) {
    return '-';
  }
};

// Göreceli zaman formatı - USA format
export const formatRelativeTime = (date) => {
  if (!date) return '-';
  
  try {
    return formatDistanceToNow(new Date(date), { 
      addSuffix: true, 
      locale: enUS 
    });
  } catch (error) {
    return '-';
  }
};

// Telefon numarası formatı - USA format
export const formatPhone = (phone) => {
  if (!phone) return '-';
  
  // Sadece rakamları al
  const cleaned = phone.replace(/\D/g, '');
  
  // USA telefon numarası formatı (+1 XXX XXX XXXX)
  if (cleaned.length === 10) {
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  } else if (cleaned.length === 12 && cleaned.startsWith('90')) {
    // Türkiye numarası için özel format
    return `+90 (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)} ${cleaned.slice(8, 10)} ${cleaned.slice(10)}`;
  }
  
  return phone;
};

// Durum etiketi
export const getStatusLabel = (status) => {
  const statusMap = {
    'yeni': 'New',
    'iletişim_kuruldu': 'Contacted',
    'teklif_verildi': 'Proposal Sent',
    'müzakere': 'Negotiation',
    'kazanıldı': 'Won',
    'kaybedildi': 'Lost'
  };
  
  return statusMap[status] || status;
};

// Öncelik etiketi
export const getPriorityLabel = (priority) => {
  const priorityMap = {
    'düşük': 'Low',
    'orta': 'Medium',
    'yüksek': 'High'
  };
  
  return priorityMap[priority] || priority;
};

// Kaynak etiketi
export const getSourceLabel = (source) => {
  const sourceMap = {
    'website': 'Website',
    'telefon': 'Phone',
    'e-posta': 'Email',
    'sosyal_medya': 'Social Media',
    'kartvizit': 'Business Card',
    'referans': 'Referral',
    'diğer': 'Other'
  };
  
  return sourceMap[source] || source;
};

// İsim formatı
export const formatName = (firstName, lastName) => {
  if (!firstName && !lastName) return '-';
  return `${firstName || ''} ${lastName || ''}`.trim();
};

// Şirket adı formatı
export const formatCompany = (company) => {
  if (!company) return '-';
  return company;
};

// E-posta formatı
export const formatEmail = (email) => {
  if (!email) return '-';
  return email.toLowerCase();
};

// Website formatı
export const formatWebsite = (website) => {
  if (!website) return '-';
  
  // http:// veya https:// ekle
  if (!website.startsWith('http://') && !website.startsWith('https://')) {
    return `https://${website}`;
  }
  
  return website;
};

// Adres formatı
export const formatAddress = (address) => {
  if (!address) return '-';
  
  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.zipCode) parts.push(address.zipCode);
  if (address.country) parts.push(address.country);
  
  return parts.length > 0 ? parts.join(', ') : '-';
};

// Dosya boyutu formatı
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Sayı formatı - USA format
export const formatNumber = (number) => {
  if (!number && number !== 0) return '-';
  
  return new Intl.NumberFormat('en-US').format(number);
};

// Yüzde formatı
export const formatPercentage = (value, total) => {
  if (!total || total === 0) return '0%';
  
  const percentage = (value / total) * 100;
  return `${percentage.toFixed(1)}%`;
};

// Metin kısaltma
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '-';
  
  if (text.length <= maxLength) return text;
  
  return text.substring(0, maxLength) + '...';
};

// Slug oluşturma
export const createSlug = (text) => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim('-');
}; 

// Status translation
export const getStatusText = (status) => {
  const statusMap = {
    'new': 'New',
    'contacted': 'Contacted',
    'proposal_sent': 'Proposal Sent',
    'quote_sent': 'Quote Sent',
    'negotiation': 'Negotiation',
    'won': 'Won',
    'lost': 'Lost',
    'do_not_call': 'Do Not Call'
  };
  return statusMap[status] || status;
};

// Priority translation
export const getPriorityText = (priority) => {
  const priorityMap = {
    'low': 'Low',
    'medium': 'Medium',
    'high': 'High'
  };
  return priorityMap[priority] || priority;
};

// Source translation
export const getSourceText = (source) => {
  const sourceMap = {
    'website': 'Website',
    'phone': 'Phone',
    'phone_agent': 'Phone Agent',
    'email': 'Email',
    'social_media': 'Social Media',
    'business_card': 'Business Card',
    'referral': 'Referral',
    'google_ads': 'Google Ads',
    'other': 'Other'
  };
  return sourceMap[source] || source;
};

// Notes source translation (for note sources: web, phone, email, appointment)
export const getNoteSourceText = (source) => {
  const sourceMap = {
    'web': 'Web',
    'phone': 'Phone',
    'phone_agent': 'Phone Agent',
    'email': 'Email',
    'sms': 'SMS',
    'appointment': 'Appointment',
    'google_ad': 'Google Ad',
  };
  return sourceMap[source] || source;
};

// Notes source color mapping
export const getNoteSourceColor = (source) => {
  const colorMap = {
    'web': 'bg-blue-100 text-blue-800',
    'phone': 'bg-green-100 text-green-800',
    'phone_agent': 'bg-cyan-100 text-cyan-800',
    'email': 'bg-purple-100 text-purple-800',
    'sms': 'bg-teal-100 text-teal-800',
    'appointment': 'bg-orange-100 text-orange-800'
  };
  return colorMap[source] || 'bg-gray-100 text-gray-800';
};

// Notes source icon mapping
export const getNoteSourceIcon = (source) => {
  const iconMap = {
    'web': '🌐',
    'phone': '📞',
    'phone_agent': '🤖',
    'email': '📧',
    'sms': '💬',
    'appointment': '📅'
  };
  return iconMap[source] || '📝';
};

// Status color mapping
export const getStatusColor = (status) => {
  const colorMap = {
    'new': 'bg-blue-100 text-blue-800',
    'contacted': 'bg-yellow-100 text-yellow-800',
    'proposal_sent': 'bg-purple-100 text-purple-800',
    'quote_sent': 'bg-indigo-100 text-indigo-800',
    'negotiation': 'bg-orange-100 text-orange-800',
    'won': 'bg-green-100 text-green-800',
    'lost': 'bg-red-100 text-red-800',
    'do_not_call': 'bg-gray-800 text-white'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

// Priority color mapping
export const getPriorityColor = (priority) => {
  const colorMap = {
    'low': 'bg-green-100 text-green-800',
    'medium': 'bg-yellow-100 text-yellow-800',
    'high': 'bg-red-100 text-red-800'
  };
  return colorMap[priority] || 'bg-gray-100 text-gray-800';
}; 